import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { CheckCircle2, Clock, Loader2, MailX, Save, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, useApi } from "@/lib/api";
import { dt } from "@/lib/format";
import { useMe } from "@/context/MeContext";
import WeeklyReportPanel from "@/components/WeeklyReportPanel";

const PRESETS = [
  { label: "Gmail", host: "smtp.gmail.com", port: 587, security: "starttls" },
  { label: "IONOS", host: "smtp.ionos.de", port: 587, security: "starttls" },
  { label: "Strato", host: "smtp.strato.de", port: 465, security: "ssl" },
  { label: "Outlook", host: "smtp.office365.com", port: 587, security: "starttls" },
  { label: "GMX", host: "mail.gmx.net", port: 587, security: "starttls" },
  { label: "WEB.DE", host: "smtp.web.de", port: 587, security: "starttls" },
];

const EMPTY = { host: "", port: 587, security: "starttls", user: "", password: "", sender: "", sender_name: "KupferRadar" };

const Field = ({ label, children, className = "" }) => (
  <div className={className}><Label className="text-xs text-slate-500">{label}</Label>{children}</div>
);

const SmtpForm = ({ smtp, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState({ ...EMPTY, ...smtp, password: "", sender_name: smtp.sender_name || "KupferRadar" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/smtp", { ...f, port: parseInt(f.port, 10) || 0 });
      toast.success("Postfach gespeichert");
      onSaved(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="smtp-form">
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button key={p.label} type="button" onClick={() => setF({ ...f, host: p.host, port: p.port, security: p.security })} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600 transition-colors hover:border-orange-300 hover:text-orange-700" data-testid={`btn-smtp-preset-${p.label.toLowerCase().replace(".", "")}`}>
            {p.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
        <Field label="SMTP-Server" className="sm:col-span-3"><Input value={f.host} onChange={set("host")} placeholder="smtp.ihr-anbieter.de" data-testid="input-smtp-host" /></Field>
        <Field label="Port" className="sm:col-span-1"><Input value={f.port} onChange={set("port")} inputMode="numeric" data-testid="input-smtp-port" /></Field>
        <Field label="Verschlüsselung" className="sm:col-span-2">
          <Select value={f.security} onValueChange={(v) => setF({ ...f, security: v })}>
            <SelectTrigger data-testid="select-smtp-security"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="starttls">STARTTLS (meist 587)</SelectItem>
              <SelectItem value="ssl">SSL/TLS (meist 465)</SelectItem>
              <SelectItem value="none">Keine</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Benutzername (meist Ihre E-Mail)" className="sm:col-span-3"><Input value={f.user} onChange={set("user")} autoComplete="off" data-testid="input-smtp-user" /></Field>
        <Field label={smtp.has_password ? "Passwort (leer lassen = unverändert)" : "Passwort / App-Passwort"} className="sm:col-span-3">
          <Input type="password" value={f.password} onChange={set("password")} autoComplete="new-password" placeholder={smtp.has_password ? "••••••••" : ""} data-testid="input-smtp-password" />
        </Field>
        <Field label="Absender-Adresse" className="sm:col-span-3"><Input value={f.sender} onChange={set("sender")} placeholder="leer = Benutzername" data-testid="input-smtp-sender" /></Field>
        <Field label="Absender-Name" className="sm:col-span-3"><Input value={f.sender_name} onChange={set("sender_name")} data-testid="input-smtp-sender-name" /></Field>
      </div>
      <p className="text-xs text-slate-500">Tipp: Bei Gmail und Outlook mit 2-Faktor-Anmeldung wird ein „App-Passwort“ benötigt, nicht das normale Passwort.</p>
      <Button onClick={save} disabled={saving} data-testid="btn-save-smtp"><Save className="mr-2 h-4 w-4" /> Postfach speichern</Button>
    </div>
  );
};

const Status = ({ smtp, onRemove }) =>
  smtp.configured ? (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm" data-testid="smtp-configured">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
        <div><div className="font-medium text-emerald-800">Postfach eingerichtet</div><div className="text-emerald-700/80">{smtp.host}:{smtp.port} · Absender {smtp.sender || smtp.user} · geändert {dt(smtp.updated_at)}</div></div>
      </div>
      <Button variant="ghost" size="icon" onClick={onRemove} title="Postfach entfernen" data-testid="btn-delete-smtp"><Trash2 className="h-4 w-4 text-red-600" /></Button>
    </div>
  ) : (
    <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="smtp-not-configured">
      <MailX className="h-5 w-5" /> Noch kein Postfach eingerichtet – Alarme werden protokolliert, aber nicht verschickt.
    </div>
  );

export default function Einstellungen() {
  const api = useApi();
  const me = useMe();
  const [smtp, setSmtp] = useState(null);
  const [to, setTo] = useState(me.email || "");
  const [sending, setSending] = useState(false);

  useEffect(() => { if (me.role === "admin") api.get("/settings/smtp").then((r) => setSmtp(r.data)); }, [api, me.role]);
  if (me.role !== "admin") return <Navigate to="/" replace />;

  const test = async () => {
    setSending(true);
    try { await api.post("/settings/test-email", { to }); toast.success("Test-E-Mail versendet"); }
    catch (e) { toast.error(errMsg(e)); }
    setSending(false);
  };
  const remove = async () => {
    try { const r = await api.delete("/settings/smtp"); setSmtp(r.data); toast.success("Postfach entfernt"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Einstellungen</h1>
        <p className="mt-1 text-sm text-slate-500">E-Mail-Postfach für Alarme und automatische Kursprüfung.</p>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="panel space-y-5 p-6 lg:col-span-2" data-testid="smtp-status-panel">
          <h3 className="text-base font-semibold">E-Mail-Postfach (SMTP)</h3>
          {!smtp ? <Loader2 className="h-5 w-5 animate-spin text-orange-600" /> : (
            <>
              <Status smtp={smtp} onRemove={remove} />
              <SmtpForm key={smtp.updated_at || "new"} smtp={smtp} onSaved={setSmtp} />
              <div className="flex flex-col gap-2 border-t border-slate-200 pt-5 sm:flex-row">
                <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Empfänger für Test" data-testid="input-test-email" />
                <Button variant="secondary" onClick={test} disabled={sending || !smtp.configured} data-testid="btn-send-test-email">
                  {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Test-E-Mail senden
                </Button>
              </div>
            </>
          )}
        </div>
        <div className="space-y-6">
        <WeeklyReportPanel />
        <div className="panel h-fit space-y-3 p-6">
          <h3 className="text-base font-semibold">Automatische Kursprüfung</h3>
          <div className="flex items-start gap-3 text-sm text-slate-600"><Clock className="h-5 w-5 shrink-0 text-orange-600" /><div>Alle 15 Minuten wird der COMEX-Kupferkurs abgerufen, in €/kg umgerechnet und mit allen aktiven Preisalarmen verglichen.</div></div>
          <p className="text-xs text-slate-500">Datenquelle: COMEX Kupfer-Future (HG) und EUR/USD über Yahoo Finance. Kurse können bis zu 15 Minuten verzögert sein. Richtpreise je Sorte sind Schätzwerte.</p>
        </div>
        </div>
      </div>
    </div>
  );
}
